import { FinishedUnaryCall, RpcOptions, ServiceInfo, UnaryCall } from '@protobuf-ts/runtime-rpc'
import { AnyAction } from '@reduxjs/toolkit'
import { useClientRPC } from 'lib/rpcts'
import { convertToRefString } from 'lib/websocket-notify'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ThunkDispatch } from 'redux-thunk'
import { action, mutatorFunction } from './actions'
import { ReduxListState, serializedError } from './reducer'
import { GRPCClient, InferListUnaryResponse, InferRequest, InferUnaryResponse } from './utils'

export type MutationState<Res extends object> = {
    inProgress: boolean
    error?: Error
    clearError: () => void
    data?: Res
    /** Whether the RPC client is ready to make requests */
    isReady: boolean
}

export type Trigger<Req extends object, TransformedRes extends object> = (
    args: Req,
    flags?: MutationHookFlags
) => Promise<MutationState<TransformedRes>>

type MutationHookFlags = RpcOptions
type CreateMutationAPI<
    Req extends object,
    TransformedRes extends object,
> = () => MutationState<TransformedRes> & Record<'mutate', Trigger<Req, TransformedRes>> & Record<'isReady', boolean>

/**
 * Creates a custom hook which can manage calling a gRPC mutation method, tracking the progress or loading state of that
 * method, and optionally adjusting data in the redux cache with the result of the mutation.
 *
 * @param {GRPCClient<Client>} ClientConstructor - The gRPC client constructor
 * @param {Method} methodName - The name of the mutation method
 * @param {(i: Res) => TransformedRes} [transformResponse] An optional transformation function
 *        that can be applied to the raw response, to manipulate the data before it's stored in the redux store.
 * @param {string} [tag] The typeName of a ref object, used to match websocket updates to this object.
 * @param {(cached: TransformedRes, new: Res | TransformedRes) => TransformedRes} [mutateRedux] - Optional function to update cached data
 *
 * @returns {CreateMutationAPI<Req, TransformedRes>} A hook that provides:
 *   - inProgress: Boolean indicating if mutation is currently in progress
 *   - error: The error that occurred during the mutation, if any
 *   - data: The result of the mutation, if the RPC returns a response
 *   - mutate: The actual function to trigger the mutation
 */
export const createMutationApi =
    <
        Client extends ServiceInfo,
        Method extends keyof Client & string,
        Req extends InferRequest<Client[Method]>,
        Res extends InferListUnaryResponse<Client[Method]> extends never ? InferUnaryResponse<Client[Method]> : Omit<InferListUnaryResponse<Client[Method]>, 'nextPageToken'>,
        TransformedRes extends object = Res,
    >(
        ClientConstructor: GRPCClient<Client>,
        methodName: Client[Method] extends (input: Req, options?: RpcOptions) => UnaryCall<Req, Res> ? Method : never,
        transformResponse?: (res: Res, req: Req) => TransformedRes,
        tag?: string,
        mutateRedux?: (cachedValue: TransformedRes, newValue: Res | TransformedRes | undefined) => TransformedRes,
    ): CreateMutationAPI<Req, TransformedRes> =>
        () => {
            const client = useClientRPC(ClientConstructor)
            const dispatch: ThunkDispatch<ReduxListState<TransformedRes>, never, AnyAction> = useDispatch()
            const ref = useRef({ client })

            // NOTE: we do this in order to _not_ break if the caller invokes the trigger inside a useCallback and doesn't capture it in the dep array.
            // TODO(kans): useClientRPC shouldn't do this to us
            useEffect(() => {
                ref.current = { client }
            }, [client, ref])

            type State = MutationState<TransformedRes> & { mutate: Trigger<Req, TransformedRes> }

            const mutate = useCallback(
                async (input: Req, flags?: MutationHookFlags) => {
                    const { client: client_ } = ref.current
                    if (!client_) {
                        throw new Error('no client')
                    }
                    let s: State = { inProgress: true, mutate, clearError: () => undefined, isReady: true }
                    const setState = (nextState: Omit<MutationState<TransformedRes>, 'clearError'>): void => {
                        s = {
                            ...nextState,
                            mutate,
                            clearError: (): void => _setState((oldState) => ({ ...oldState, error: undefined })),
                        }
                        _setState(s)
                    }

                    setState({ ...s, inProgress: true })

                    let res: FinishedUnaryCall<Req, Res>
                    const m = client_[methodName] as (i: Req, options?: RpcOptions) => UnaryCall<Req, Res>
                    try {
                        res = await m.call(client_, input, flags)
                    } catch (e) {
                        setState({ inProgress: false, error: serializedError(e), isReady: true })
                        throw e
                    }
                    const data = res.response

                    const transformed = transformResponse ? transformResponse(data, input) : data

                    setState({ inProgress: false, data: transformed as TransformedRes, isReady: true })

                    if (!tag) {
                        return s
                    }

                    if ('list' in transformed && Array.isArray(transformed.list)) {
                        (transformed.list as Array<Res>).forEach((v) => {
                            const refString = convertToRefString(v, tag)
                            if (!refString) {
                                throw new Error('mutate from list: cannot convert to ref')
                            }

                            dispatch(action.invalidateTag(tag || '', refString, v, mutateRedux as mutatorFunction))
                        })
                    } else {
                        const refString = convertToRefString(transformed, tag)
                        if (refString === null) {
                            throw new Error('mutate: cannot convert to ref')
                        }

                        dispatch(action.invalidateTag(tag || '', refString, transformed, mutateRedux as mutatorFunction))
                    }

                    return s
                },
                [dispatch],
            )
            const [_state, _setState] = useState<State>({
                inProgress: false,
                mutate,
                clearError: () => undefined,
                isReady: false,
            })

            const clientReady = !!client
            useEffect(() => {
                if (clientReady && !_state.isReady) {
                    _setState((prev) => ({ ...prev, isReady: true }))
                }
            }, [clientReady, _state.isReady])

            return _state
        }
